/* eslint-disable */
import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useRef,
    useMemo,
} from "react";
import { isPlatform } from "@ionic/react";
import { Network } from "@capacitor/network";
import config from "../../config";
import FastAPIClient from "../../client";
const client = new FastAPIClient(config);

const iosOrAndroid = isPlatform("hybrid");

const NetworkContext = createContext();

// Singleton WebSocket instance
let globalWsInstance = null;
let globalIsConnecting = false;
let globalProviderCount = 0;

export function NetworkProvider({ children }) {
    const [isOnline, setIsOnline] = useState(true);
    const [user, setUser] = useState(null);
    const [locale, setLocale] = useState("en");
    const [ws, setWs] = useState(null);
    const [isWsConnected, setIsWsConnected] = useState(false);
    const [wsError, setWsError] = useState(null);

    // Notification states
    const [notifications, setNotifications] = useState([]);
    const [notificationsLoading, setNotificationsLoading] = useState(true);
    const [notificationsInitialized, setNotificationsInitialized] =
        useState(false);
    const [notificationTypes, setNotificationTypes] = useState([]);
    const [messagesNotifications, setMessagesNotifications] = useState([]);
    const [notificationUsers, setNotificationUsers] = useState([]);

    const reconnectTimeoutRef = useRef(null);
    const providerIdRef = useRef(Math.random().toString(36).substring(7));

    // Add a ref to track current online status for closures
    const isOnlineRef = useRef(true);

    // Keep the ref in sync with the state
    useEffect(() => {
        isOnlineRef.current = isOnline;

        // Clear any pending reconnection timeouts when going offline
        if (!isOnline && reconnectTimeoutRef.current) {
            console.log(
                `[${providerIdRef.current}] Clearing reconnection timeout due to offline status`
            );
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        // Close WebSocket connection when going offline
        if (!isOnline && globalWsInstance) {
            console.log(
                `[${providerIdRef.current}] Closing WebSocket due to offline status`
            );
            try {
                globalWsInstance.close();
            } catch (error) {
                console.error(
                    `[${providerIdRef.current}] Error closing WebSocket when going offline:`,
                    error
                );
            }
            globalWsInstance = null;
            setWs(null);
            setIsWsConnected(false);
        }
    }, [isOnline]);

    // Helper function to replace placeholders in messages
    const replacePlaceholders = async (
        messageTemplate,
        notificationData,
        users = notificationUsers
    ) => {
        let data;

        // Handle both object and string data
        if (typeof notificationData === "string") {
            try {
                data = JSON.parse(notificationData);
            } catch (error) {
                console.warn("Failed to parse notification data:", error);
                return messageTemplate;
            }
        } else if (
            typeof notificationData === "object" &&
            notificationData !== null
        ) {
            data = notificationData;
        } else {
            console.warn(
                "Invalid notification data type:",
                typeof notificationData
            );
            return messageTemplate;
        }

        if (!messageTemplate || typeof messageTemplate !== "string") {
            return messageTemplate;
        }

        let processedMessage = messageTemplate;
        let creatorData = null;
        let clientData = null;

        // Use passed users parameter instead of state
        if (data.created_by) {
            creatorData = users.find(
                (user) => user.user_id === data.created_by
            );

            // Fallback values if user not found
            if (!creatorData) {
                creatorData = {
                    first_name: "Un utilisateur",
                    last_name: "",
                };
            }
        }

        // Safely fetch client data (still need API call for this)
        if (data.client_id) {
            try {
                clientData = await client.getClient(data.client_id);
            } catch (error) {
                console.warn(
                    "Failed to fetch client data for client_id:",
                    data.client_id,
                    error
                );
                // Provide fallback values when access is restricted
                clientData = {
                    name: data.client_name || "un client",
                    client_name: data.client_name || "un client",
                    city: data.city || "une ville",
                    company_name: data.company_name || "une entreprise",
                };
            }
        }

        // Replace user-related placeholders (with and without HTML tags)
        if (creatorData) {
            // Replace {first_name} and {last_name} individually
            processedMessage = processedMessage.replace(
                /{first_name}/g,
                creatorData.first_name || "Un utilisateur"
            );
            processedMessage = processedMessage.replace(
                /{last_name}/g,
                creatorData.last_name || ""
            );

            // Handle combined patterns like "<b>{first_name} {last_name}</b>"
            const fullName = `${creatorData.first_name || "Un utilisateur"} ${
                creatorData.last_name || ""
            }`.trim();
            processedMessage = processedMessage.replace(
                /<b>\{first_name\}\s*\{last_name\}<\/b>/g,
                `<b>${fullName}</b>`
            );
        } else {
            // Fallback when no creator data is available
            processedMessage = processedMessage.replace(
                /{first_name}/g,
                "Un utilisateur"
            );
            processedMessage = processedMessage.replace(/{last_name}/g, "");

            // Handle combined patterns with fallback
            processedMessage = processedMessage.replace(
                /<b>\{first_name\}\s*\{last_name\}<\/b>/g,
                "<b>Un utilisateur</b>"
            );
        }

        // Replace client-related placeholders (with and without HTML tags)
        if (clientData) {
            const clientName =
                clientData.name || clientData.client_name || "un client";
            const cityName = clientData.city || "une ville";
            const companyName = clientData.company_name || "une entreprise";

            // Individual replacements
            processedMessage = processedMessage.replace(
                /{client_name}/g,
                clientName
            );
            processedMessage = processedMessage.replace(/{city}/g, cityName);
            processedMessage = processedMessage.replace(
                /{company_name}/g,
                companyName
            );

            // Handle combined patterns like "<b>{client_name} ({city})</b>"
            processedMessage = processedMessage.replace(
                /<b>\{client_name\}\s*\(\{city\}\)<\/b>/g,
                `<b>${clientName} (${cityName})</b>`
            );
        } else {
            // Fallback when no client data is available
            const fallbackClientName = data.client_name || "un client";
            const fallbackCity = data.city || "une ville";
            const fallbackCompany = data.company_name || "une entreprise";

            // Individual replacements
            processedMessage = processedMessage.replace(
                /{client_name}/g,
                fallbackClientName
            );
            processedMessage = processedMessage.replace(
                /{city}/g,
                fallbackCity
            );
            processedMessage = processedMessage.replace(
                /{company_name}/g,
                fallbackCompany
            );

            // Handle combined patterns with fallback
            processedMessage = processedMessage.replace(
                /<b>\{client_name\}\s*\(\{city\}\)<\/b>/g,
                `<b>${fallbackClientName} (${fallbackCity})</b>`
            );
        }

        // Replace any remaining common placeholders
        processedMessage = processedMessage.replace(
            /{lead_name}/g,
            data.lead_name || data.client_name || "un prospect"
        );
        processedMessage = processedMessage.replace(
            /{product_name}/g,
            data.product_name || "un produit"
        );

        return processedMessage;
    };

    // Computed value for new notifications
    const newNotifications = useMemo(() => {
        return notifications.filter((notification) => !notification.is_read);
    }, [notifications]);

    // Fetch notification types on mount
    useEffect(() => {
        const fetchNotificationTypes = async () => {
            try {
                const types = await client.getNotificationTypes();
                setNotificationTypes(types);
            } catch (error) {
                console.error("Error fetching notification types:", error);
            }
        };

        fetchNotificationTypes();
    }, []);

    // Track provider instances
    useEffect(() => {
        globalProviderCount++;

        return () => {
            globalProviderCount--;

            // If this is the last provider, clean up the WebSocket
            if (globalProviderCount === 0) {
                if (globalWsInstance) {
                    try {
                        globalWsInstance.close();
                    } catch (error) {
                        console.error(
                            "Error closing WebSocket on last provider unmount:",
                            error
                        );
                    }
                    globalWsInstance = null;
                }
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                }
            }
        };
    }, []);

    const fetchUser = async () => {
        const user = await client.fetchUser();
        setUser(user);
    };

    useEffect(() => {
        fetchUser();
        setLocale(localStorage.getItem("language"));
        let networkListener = undefined;

        const checkInitialStatus = async () => {
            try {
                if (iosOrAndroid) {
                    const status = await Network.getStatus();
                    setIsOnline(status.connected);
                }
            } catch (error) {
                console.error("Error checking initial network status:", error);
            }
        };

        checkInitialStatus();

        if (iosOrAndroid) {
            networkListener = Network.addListener(
                "networkStatusChange",
                (status) => {
                    setIsOnline(status.connected);
                }
            );
        } else {
            window.addEventListener("online", () => setIsOnline(true));
            window.addEventListener("offline", () => setIsOnline(false));

            return () => {
                window.removeEventListener("online", () => setIsOnline(true));
                window.removeEventListener("offline", () => setIsOnline(false));
            };
        }

        return () => {
            if (networkListener) networkListener.remove();
        };
    }, []);

    // Helper function to handle new notifications from WebSocket
    const handleNewWebSocketNotification = async (notificationData) => {
        // Get the message template for this notification type
        const notificationType = notificationTypes.find(
            (type) => type.id === notificationData.type_id
        );

        const notificationWithTemplate = {
            ...notificationData,
            message: notificationType?.message || null,
        };

        // Check if this is a "general" notification type
        if (notificationData.type_id === "general") {
            // For general notifications, extract message directly from data
            let parsedData;
            try {
                parsedData =
                    typeof notificationData.data === "string"
                        ? JSON.parse(notificationData.data)
                        : notificationData.data;
            } catch (error) {
                console.warn(
                    "Failed to parse WebSocket notification data:",
                    error
                );
                parsedData = {};
            }

            // Use the message directly from the notification data
            const directMessage =
                parsedData.message || "Nouvelle notification générale";
            const enhancedNotification = {
                ...notificationData,
                message: directMessage,
            };

            setNotifications((prev) => [enhancedNotification, ...prev]);
            return;
        }

        // Check if we need to fetch the user for this notification
        let parsedData;
        try {
            parsedData =
                typeof notificationData.data === "string"
                    ? JSON.parse(notificationData.data)
                    : notificationData.data;
        } catch (error) {
            console.warn("Failed to parse WebSocket notification data:", error);
            parsedData = {};
        }

        // Check if we already have the user data
        const createdBy = parsedData.created_by;

        setNotificationUsers((prevUsers) => {
            const existingUser = prevUsers.find(
                (user) => user.user_id === createdBy
            );

            if (!existingUser && createdBy) {
                // Fetch the new user and add to notification users
                client
                    .getUser(createdBy)
                    .then((newUser) => {
                        if (newUser) {
                            setNotificationUsers((prev) => {
                                // Check again to avoid duplicates
                                const stillExists = prev.find(
                                    (u) => u.user_id === createdBy
                                );
                                if (!stillExists) {
                                    return [...prev, newUser];
                                }
                                return prev;
                            });
                        }
                    })
                    .catch((error) => {
                        console.warn(
                            `Failed to fetch user ${createdBy} for WebSocket notification:`,
                            error
                        );
                    });
            }

            return prevUsers;
        });

        // Process the message template
        if (notificationType?.message) {
            let parsedMessage;
            try {
                parsedMessage = JSON.parse(notificationType.message);
            } catch (e) {
                console.warn("Failed to parse message template:", e);
                parsedMessage = notificationType.message;
            }

            try {
                // Get current notification users from state
                setNotificationUsers((currentUsers) => {
                    // Use the current users for processing the message
                    replacePlaceholders(
                        parsedMessage,
                        notificationData.data,
                        currentUsers
                    )
                        .then((processedMessage) => {
                            const enhancedNotification = {
                                ...notificationData,
                                message: processedMessage,
                            };

                            setNotifications((prev) => [
                                enhancedNotification,
                                ...prev,
                            ]);
                        })
                        .catch((error) => {
                            console.error(
                                "Error processing WebSocket notification message:",
                                error
                            );
                            setNotifications((prev) => [
                                notificationWithTemplate,
                                ...prev,
                            ]);
                        });

                    return currentUsers; // Return unchanged users
                });
            } catch (error) {
                console.error(
                    "Error processing WebSocket notification message:",
                    error
                );
                setNotifications((prev) => [notificationWithTemplate, ...prev]);
            }
        } else {
            setNotifications((prev) => [notificationWithTemplate, ...prev]);
        }
    };

    // WebSocket connection management
    useEffect(() => {
        const connectWebSocket = () => {
            // Don't connect if we're offline, already connecting, or don't have a user
            if (!isOnlineRef.current || globalIsConnecting || !user?.user_id) {
                console.log(
                    `[${providerIdRef.current}] Not connecting WebSocket:`,
                    !isOnlineRef.current
                        ? "offline"
                        : globalIsConnecting
                        ? "already connecting"
                        : "no user"
                );
                return;
            }

            try {
                globalIsConnecting = true;
                const token = localStorage.getItem("token");
                const wsUrl = `${config.apiBasePath.replace(
                    "http",
                    "ws"
                )}/ws/notifications/${user.user_id}?token=${token}`;

                // Close existing connection if any
                if (globalWsInstance) {
                    try {
                        globalWsInstance.close();
                    } catch (error) {
                        console.error(
                            `[${providerIdRef.current}] Error closing existing WebSocket:`,
                            error
                        );
                    }
                }

                const wsInstance = new WebSocket(wsUrl);
                globalWsInstance = wsInstance;

                wsInstance.onopen = () => {
                    setIsWsConnected(true);
                    setWsError(null);
                    globalIsConnecting = false;
                    setWs(wsInstance);
                };

                wsInstance.onmessage = (event) => {
                    try {
                        const message = JSON.parse(event.data);

                        if (message.type === "notification") {
                            if (message.action === "new") {
                                // Validate the notification data structure
                                if (
                                    !message.data.notification_id ||
                                    !message.data.created_at
                                ) {
                                    console.warn(
                                        "Invalid notification data:",
                                        message.data
                                    );
                                    return;
                                }

                                // Use the new helper function
                                handleNewWebSocketNotification(message.data);
                            } else if (message.action === "delete") {
                                // Delete notification from the list
                                setNotifications((prev) =>
                                    prev.filter(
                                        (notification) =>
                                            notification.notification_id !==
                                            message.data.notification_id
                                    )
                                );
                            } else if (message.action === "update") {
                                // Update notification in the list
                                setNotifications((prev) =>
                                    prev.map((notification) => {
                                        if (
                                            notification.notification_id ===
                                            message.data.notification_id
                                        ) {
                                            // Preserve the existing processed message if it exists
                                            // Only update other fields from the WebSocket message
                                            return {
                                                ...message.data,
                                                message:
                                                    notification.message ||
                                                    message.data.message,
                                            };
                                        }
                                        return notification;
                                    })
                                );
                            }
                        }
                    } catch (error) {
                        console.error(
                            "Error handling WebSocket message:",
                            error
                        );
                    }
                };

                wsInstance.onclose = (event) => {
                    console.log(
                        `[${providerIdRef.current}] WebSocket disconnected:`,
                        event.code,
                        event.reason
                    );
                    setIsWsConnected(false);
                    setWs(null);
                    globalIsConnecting = false;
                    globalWsInstance = null;

                    // Only attempt to reconnect if we're still online and this is the last provider
                    if (globalProviderCount === 1) {
                        reconnectTimeoutRef.current = setTimeout(() => {
                            // Check current online status when timeout fires, not the stale closure value
                            if (isOnlineRef.current && user?.user_id) {
                                connectWebSocket();
                            }
                        }, 5000);
                    }
                };

                wsInstance.onerror = (error) => {
                    console.error(
                        `[${providerIdRef.current}] WebSocket error:`,
                        error
                    );
                    setWsError(error);
                    setIsWsConnected(false);
                    setWs(null);
                    globalIsConnecting = false;
                    globalWsInstance = null;
                };
            } catch (error) {
                console.error(
                    `[${providerIdRef.current}] Error creating WebSocket connection:`,
                    error
                );
                setWsError(error);
                setWs(null);
                globalIsConnecting = false;
                globalWsInstance = null;
            }
        };

        // Connect WebSocket when online and we have a user
        if (isOnline && user?.user_id) {
            connectWebSocket();
        }

        // Cleanup function
        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            // Only close the WebSocket if this is the last provider
            if (globalProviderCount === 1 && globalWsInstance) {
                try {
                    if (globalWsInstance.readyState === WebSocket.OPEN) {
                        globalWsInstance.close();
                    }
                } catch (error) {
                    console.error(
                        `[${providerIdRef.current}] Error closing WebSocket:`,
                        error
                    );
                }
                globalWsInstance = null;
            }
        };
    }, [isOnline, user?.user_id]); // Only re-run when network status or user ID changes

    const fetchNotificationUsers = async (notifications) => {
        // Extract unique user IDs from notifications
        const uniqueUserIds = new Set();

        notifications.forEach((notification) => {
            const notifData = notification.data;

            let parsedData;
            if (typeof notifData === "string") {
                try {
                    parsedData = JSON.parse(notifData);
                } catch (error) {
                    console.warn("Failed to parse notification data:", error);
                    return;
                }
            } else {
                parsedData = notifData;
            }

            if (parsedData?.created_by) {
                uniqueUserIds.add(parsedData.created_by);
            }
        });

        if (uniqueUserIds.size === 0) {
            setNotificationsLoading(false);
            return;
        }

        // Fetch user data for unique user IDs only
        const uniqueUsers = await Promise.all(
            Array.from(uniqueUserIds).map(async (userId) => {
                try {
                    const user = await client.getUser(userId);
                    return user;
                } catch (error) {
                    console.warn(`Failed to fetch user ${userId}:`, error);
                    return null;
                }
            })
        );

        // Filter out null values (failed fetches) and set unique users
        const validUsers = uniqueUsers.filter((user) => user !== null);
        setNotificationUsers(validUsers);

        // Process notifications with messages now that users are available
        await processNotificationsWithMessages(notifications, validUsers);
    };

    // Process notifications with dynamic messages after notification users are available
    const processNotificationsWithMessages = async (notifications, users) => {
        if (!notifications.length) {
            setNotificationsLoading(false);
            return;
        }

        try {
            const dynamicNotifications = await Promise.all(
                notifications.map(async (notification) => {
                    // Check if this is a "general" notification type
                    if (notification.type_id === "general") {
                        // For general notifications, extract message directly from data
                        let parsedData;
                        try {
                            parsedData =
                                typeof notification.data === "string"
                                    ? JSON.parse(notification.data)
                                    : notification.data;
                        } catch (error) {
                            console.warn(
                                "Failed to parse notification data:",
                                error
                            );
                            parsedData = {};
                        }

                        // Use the message directly from the notification data
                        const directMessage =
                            parsedData.message ||
                            "Nouvelle notification générale";
                        return {
                            ...notification,
                            message: directMessage,
                        };
                    }

                    let messageTemplate;

                    // Try to parse the message template from notification type
                    if (notification.message) {
                        try {
                            messageTemplate = JSON.parse(notification.message);
                        } catch (e) {
                            console.warn(
                                "Failed to parse message template:",
                                e
                            );
                            messageTemplate = notification.message;
                        }
                    }

                    // If no message template found, create a default based on notification type
                    if (!messageTemplate) {
                        if (notification.type_id === "lead_create") {
                            messageTemplate =
                                "{first_name} {last_name} a créé un nouveau prospect {client_name}";
                        } else if (notification.type_id === "lead_share") {
                            messageTemplate =
                                "{first_name} {last_name} a partagé le prospect {client_name} ({city}) avec vous.";
                        } else {
                            messageTemplate =
                                "Nouvelle notification de {first_name} {last_name}";
                        }
                    }

                    // Replace placeholders in the message with actual data
                    try {
                        const processedMessage = await replacePlaceholders(
                            messageTemplate,
                            notification.data,
                            users
                        );
                        return {
                            ...notification,
                            message: processedMessage,
                        };
                    } catch (error) {
                        console.error(
                            "Error processing notification message:",
                            error
                        );
                        return {
                            ...notification,
                            message: `Notification de type ${notification.type_id}`,
                        };
                    }
                })
            );

            setNotifications(dynamicNotifications);
            setNotificationsLoading(false);
        } catch (error) {
            console.error(
                "Error processing notifications with messages:",
                error
            );
            setNotificationsLoading(false);
        }
    };

    // Fetch user notifications
    const getUserNotifications = async () => {
        if (user?.user_id && isOnline) {
            setNotificationsLoading(true);
            setNotificationsInitialized(true);
            try {
                const data = await client.getUserNotifications(user.user_id);
                const notificationsData = data?.results || [];

                const notificationsWithMessages = notificationsData.map(
                    (notification) => {
                        const notificationType = notificationTypes.find(
                            (type) => type.id === notification.type_id
                        );
                        // Get message template from notification type, not from notification itself
                        const message = notificationType?.message || null;
                        return { ...notification, message };
                    }
                );

                // Set notifications first (without processed messages)
                setNotifications(notificationsWithMessages);

                // Fetch notification users based on the notifications
                await fetchNotificationUsers(notificationsWithMessages);
            } catch (error) {
                console.error("Error fetching notifications:", error);
                setNotifications([]);
                setNotificationsLoading(false);
            }
        } else {
            // If we can't fetch notifications (no user or offline), stop loading
            setNotificationsLoading(false);
            setNotificationsInitialized(true);
        }
    };

    // Fetch messages notifications
    const getMessagesNotifications = async (userId) => {
        if (isOnline) {
            try {
                const data = await client.getMessagesNotifications(userId);
                setMessagesNotifications(data?.notifications || []);
            } catch (error) {
                console.error("Error fetching messages notifications:", error);
            }
        }
    };

    // Fetch notifications when user is available and notification types are loaded
    useEffect(() => {
        if (user?.user_id && notificationTypes.length > 0 && isOnline) {
            getUserNotifications();
            getMessagesNotifications(user.user_id);

            // Set up polling for messages notifications
            const interval = setInterval(() => {
                if (isOnline) {
                    getMessagesNotifications(user.user_id);
                }
            }, 30000);

            return () => clearInterval(interval);
        }
    }, [user?.user_id, notificationTypes, isOnline]);

    return (
        <NetworkContext.Provider
            value={{
                // Network state
                isOnline,
                locale,
                ws,
                isWsConnected,
                wsError,
                // User state
                user,
                // Notification state
                notifications,
                setNotifications,
                notificationsLoading,
                setNotificationsLoading,
                notificationsInitialized,
                notificationTypes,
                notificationUsers,
                messagesNotifications,
                setMessagesNotifications,
                newNotifications,
                // Notification functions
                getUserNotifications,
                getMessagesNotifications,
                replacePlaceholders,
            }}
        >
            {children}
        </NetworkContext.Provider>
    );
}

export function useNetwork() {
    return useContext(NetworkContext);
}
